import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../locales/i18n";

const API_URL_RESTAURANTS = "https://dev.api.jomuntu.com/api/v1/food-venues";

interface Restaurant {
  createdAt: any;
  id: number;
  name: string;
  emailAddress: string;
  phoneNumber: string;
  adminName: string;
  ownerName: string;
  ownerEmail: string;
  openingHours: string;
  location: string;
  category: string;
  logoUrl: string;
  email: string;
}

// interface Pageable {
//   pageNumber: number;
//   pageSize: number;
//   totalPages: number;
//   totalElements: number;
// }

// interface FetchRestaurantsResponse {
//   content: Restaurant[];
//   pageable: Pageable;
//   totalPages: number;
//   totalElements: number;
// }

interface RestaurantsState {
  restaurants: Restaurant[];
  loading: boolean;
  error: string | null;
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

const initialState: RestaurantsState = {
  restaurants: [],
  loading: false,
  error: null,
  page: 0,
  size: 10,
  totalPages: 0,
  totalElements: 0,
};

export const fetchRestaurants = createAsyncThunk(
  "restaurants/fetchRestaurants",
  async (
    {
      role,
      page,
      size,
      name,
    }: { role?: string; page: number; size: number; name?: string },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("token");

      const params: any = {
        role,
        page,
        size,
        status: "APPROVED",
      };

      if (name) {
        params.name = name;
      } else {
        params.name = "";
      }

      const response = await axios.get(
        `${API_URL_RESTAURANTS}/by-name-and-status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(i18n.t("errors.fetchRestaurants"));
    }
  }
);

export const fetchRestaurantsStatus = createAsyncThunk(
  "restaurants/fetchRestaurantsStatus",
  async (
    {
      role,
      page,
      size,
      name,
    }: { role?: string; page: number; size: number; name?: string },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("token");

      const params: any = {
        role,
        page,
        size,
        status: "PENDING",
      };

      if (name) {
        params.name = name;
      }

      const response = await axios.get(
        `${API_URL_RESTAURANTS}/by-name-and-status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(i18n.t("errors.fetchRestaurants"));
    }
  }
);

export const fetchRestaurantsSearch = createAsyncThunk(
  "restaurants/fetchRestaurantsSearch",
  async (
    {
      role,
      page,
      size,
      name,
    }: { role?: string; page: number; size: number; name?: string },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("token");

      const params: any = {
        role,
        page,
        size,
      };

      if (name) {
        params.name = name;
      }

      const response = await axios.get(
        `${API_URL_RESTAURANTS}/by-name-and-status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(i18n.t("errors.fetchRestaurants"));
    }
  }
);

export const deleteRestaurant = createAsyncThunk(
  "allUsers/deleteRestaurant",
  async (id: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL_RESTAURANTS}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return id;
    } catch (error) {
      return rejectWithValue(i18n.t("errors.deleteRestaurant"));
    }
  }
);

export const createRestaurant = createAsyncThunk(
  "restaurants/createRestaurant",
  async (
    {
      name,
      emailAddress,
      phoneNumber,
      ownerName,
      ownerEmail,
      openingHours,
      location,
      category,
      image,
    }: {
      name: string;
      emailAddress: string;
      phoneNumber: string;
      ownerName: string;
      ownerEmail: string;
      openingHours: string;
      location: string;
      category: string;
      image: File | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue(i18n.t("errors.tokenNotFound"));
      }

      // Step 1: Create the restaurant
      const response = await axios.post(
        API_URL_RESTAURANTS,
        {
          name,
          emailAddress,
          phoneNumber,
          adminName: "Admin",
          ownerName,
          ownerEmail,
          openingHours,
          location,
          category,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const restaurantId = response.data;

      // Step 2: Upload the image if present
      if (image) {
        const formData = new FormData();
        formData.append("image", image);

        await axios.post(
          `${API_URL_RESTAURANTS}/${restaurantId}/upload-image`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      return restaurantId; // Return the restaurant ID after creation
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || i18n.t("errors.createRestaurant")
      );
    }
  }
);

export const createRestaurantUser = createAsyncThunk(
  "restaurants/createRestaurantUser",
  async (
    {
      name,
      emailAddress,
      phoneNumber,
      ownerName,
      adminName,
      ownerEmail,
      openingHours,
      location,
      category,
      image,
    }: {
      name: string;
      emailAddress: string;
      adminName: string;
      phoneNumber: string;
      ownerName: string;
      ownerEmail: string;
      openingHours: string;
      location: string;
      category: string;
      image: File | null;
    },
    { rejectWithValue }
  ) => {
    const API_URL_SIGNUP =
      "https://dev.api.jomuntu.com/api/v1/food-venues/sign-up";

    try {
      const response = await axios.post(
        API_URL_SIGNUP,
        {
          name,
          emailAddress,
          phoneNumber,
          adminName,
          ownerName,
          ownerEmail,
          openingHours,
          location,
          category,
        }
        // {
        //   headers: {
        //     'Content-Type': 'application/json',
        //   }
        // }
      );

      const signupResponseData = response.data;
      const restaurantId = signupResponseData.id || signupResponseData;

      if (image && restaurantId) {
        const token = localStorage.getItem("token");

        if (!token) {
          console.warn("Token not found for image upload.");
        }

        const formData = new FormData();
        formData.append("image", image);

        const IMAGE_UPLOAD_URL = `${API_URL_RESTAURANTS}/${restaurantId}/upload-image`;

        await axios.post(IMAGE_UPLOAD_URL, formData, {

        });
        // console.log("Image uploaded successfully.");
      } else if (image && !restaurantId) {
        // console.warn(
        //   "Image provided but no restaurant ID obtained from signup to upload image."
        // );
      }

      // console.log("Restaurant user created successfully:", signupResponseData);
      return signupResponseData;
    } catch (err: any) {
      // console.error(
      //   "createRestaurantUser API error:",
      //   err.response?.data || err.message || err
      // );
      return rejectWithValue(
        err.response?.data?.message || i18n.t("errors.createRestaurantUser")
      );
    }
  }
);

export const approveRestaurant = createAsyncThunk(
  "restaurants/approveRestaurant",
  async (id: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL_RESTAURANTS}/${id}/approve`,
        {},
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(i18n.t("error.approveRestaurant"));
    }
  }
);

export const declineRestaurant = createAsyncThunk(
  "restaurants/declineRestaurant",
  async (id: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL_RESTAURANTS}/${id}/decline`,
        {},
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(i18n.t("error.declineRestaurant"));
    }
  }
);

const restaurantsSlice = createSlice({
  name: "restaurants",
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRestaurants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRestaurants.fulfilled, (state, action) => {
        state.loading = false;
        state.restaurants = action.payload.content;
        state.totalPages = action.payload.totalPages;
        state.totalElements = action.payload.totalElements;
      })
      .addCase(fetchRestaurantsStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRestaurantsStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.restaurants = action.payload.content;
        state.totalPages = action.payload.totalPages;
        state.totalElements = action.payload.totalElements;
        state.page = action.meta.arg.page;
        state.size = action.meta.arg.size;
      })
      .addCase(fetchRestaurantsSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.restaurants = action.payload.content;
        state.totalPages = action.payload.totalPages;
        state.totalElements = action.payload.totalElements;
      })
      .addCase(fetchRestaurants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteRestaurant.fulfilled, (state, action) => {
        state.restaurants = state.restaurants.filter(
          (restaurant) => restaurant.id !== action.payload
        );
      })
      .addCase(deleteRestaurant.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(createRestaurant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRestaurant.fulfilled, (state, action) => {
        state.loading = false;
        // Instead of adding an empty object, push the full restaurant object.
        const newRestaurant = action.payload; // Assuming the response contains the full restaurant object.
        state.restaurants.push(newRestaurant);
      })
      .addCase(createRestaurant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createRestaurantUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRestaurantUser.fulfilled, (state, action) => {
        state.loading = false;
        state.restaurants.push(action.payload);
      })
      .addCase(createRestaurantUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(approveRestaurant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveRestaurant.fulfilled, (state, action) => {
        state.loading = false;
        const updatedRestaurant = action.payload;
        const index = state.restaurants.findIndex(
          (restaurant) => restaurant.id === updatedRestaurant.id
        );
        if (index !== -1) {
          state.restaurants[index] = updatedRestaurant;
        }
      })
      .addCase(approveRestaurant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(declineRestaurant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(declineRestaurant.fulfilled, (state, action) => {
        state.loading = false;
        const updatedRestaurant = action.payload;
        const index = state.restaurants.findIndex(
          (restaurant) => restaurant.id === updatedRestaurant.id
        );
        if (index !== -1) {
          state.restaurants[index] = updatedRestaurant;
        }
      })
      .addCase(declineRestaurant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPage } = restaurantsSlice.actions;

export default restaurantsSlice.reducer;
